/**
 * FinanceData Dashboard - Integrated AlexFinanceData into MultibotdashboardV7
 * Fetches real data from backend API
 */

import { useEffect, useMemo, useState } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Newspaper, 
  Activity,
  BarChart3,
  Globe,
  BookOpen,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Trash2,
  Plus
} from 'lucide-react';
import { financeColors } from '../../styles/theme';
import { api } from '../../services/api';
import { useTranslation } from '../../i18n/I18nContext';

// Types
interface CryptoPrice {
  id: number;
  coin_id: string;
  symbol: string;
  name: string;
  price_usd: number;
  change_24h_pct: number;
  market_cap: number;
  volume_24h: number;
}

interface Stock {
  id: number;
  symbol: string;
  name: string;
  price: number;
  change_percent: number;
  volume: number;
  sector?: string;
}

interface StockWatchlistItem {
  symbol: string;
  created_at: string;
}

interface NewsItem {
  id: number;
  title: string;
  source: string;
  url?: string;
  symbol?: string;
  category: string;
  published_at: string;
}

interface BybitOrderbook {
  id: number;
  symbol: string;
  best_bid?: number;
  best_ask?: number;
  mid_price?: number;
  spread_pct?: number;
  bid_depth?: number;
  ask_depth?: number;
  imbalance?: number;
}

interface BinanceOrderbook {
  id: number;
  symbol: string;
  best_bid?: number;
  best_ask?: number;
  mid_price?: number;
  spread_pct?: number;
  bid_depth?: number;
  ask_depth?: number;
  imbalance?: number;
}

interface EconomicIndicator {
  id: number;
  indicator_id: string;
  name: string;
  value: number;
  date?: string;
}

interface SyncStatus {
  [key: string]: {
    source: string;
    status: string;
    records_processed: number;
    error_message?: string;
    created_at: string;
  }
}

const READ_NEWS_STORAGE_KEY = 'finance_read_news_ids';

export function FinanceData() {
  const { t, language } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllCrypto, setShowAllCrypto] = useState(false);
  const [cryptoSortKey, setCryptoSortKey] = useState<'rank' | 'coin' | 'price' | 'change24h' | 'volume24h' | 'marketCap'>('volume24h');
  const [cryptoSortDirection, setCryptoSortDirection] = useState<'asc' | 'desc'>('desc');
  const [stockSortKey, setStockSortKey] = useState<'symbol' | 'name' | 'sector' | 'price' | 'change' | 'volume'>('symbol');
  const [stockSortDirection, setStockSortDirection] = useState<'asc' | 'desc'>('asc');
  const [newStockSymbol, setNewStockSymbol] = useState('');
  const [stockActionMessage, setStockActionMessage] = useState<string | null>(null);
  const [readNewsIds, setReadNewsIds] = useState<number[]>([]);

  // Real data from API
  const [cryptoData, setCryptoData] = useState<CryptoPrice[]>([]);
  const [stocksData, setStocksData] = useState<Stock[]>([]);
  const [stockWatchlist, setStockWatchlist] = useState<StockWatchlistItem[]>([]);
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [bybitData, setBybitData] = useState<BybitOrderbook[]>([]);
  const [binanceData, setBinanceData] = useState<BinanceOrderbook[]>([]);
  const [economicData, setEconomicData] = useState<EconomicIndicator[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({});

  useEffect(() => {
    try {
      const stored = localStorage.getItem(READ_NEWS_STORAGE_KEY);
      if (!stored) {
        return;
      }
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        setReadNewsIds(parsed.filter((value): value is number => typeof value === 'number'));
      }
    } catch {
      localStorage.removeItem(READ_NEWS_STORAGE_KEY);
    }
  }, []);

  const persistReadNewsIds = (ids: number[]) => {
    setReadNewsIds(ids);
    localStorage.setItem(READ_NEWS_STORAGE_KEY, JSON.stringify(ids));
  };

  const markNewsAsRead = (newsId: number) => {
    if (readNewsIds.includes(newsId)) {
      return;
    }
    persistReadNewsIds([...readNewsIds, newsId]);
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    setError(null);
  
    try {
      // Fetch all data in parallel
      const [
        cryptoRes,
        stocksRes,
        stockWatchlistRes,
        newsRes,
        bybitRes,
        binanceRes,
        economicRes,
        syncRes
      ] = await Promise.allSettled([
        api.get('/finance/crypto/prices?limit=100'),
        api.get('/finance/stocks?limit=20'),
        api.get('/finance/stocks/watchlist'),
        api.get('/finance/news?limit=20'),
        api.get('/finance/bybit/orderbook'),
        api.get('/finance/binance/orderbook'),
        api.get('/finance/economic'),
        api.get('/finance/sync/status')
      ]);

      console.log('Crypto response:', cryptoRes);

      if (cryptoRes.status === 'fulfilled') {
        const response = cryptoRes.value;
        const data = response.data || response;
        console.log('Crypto data extracted:', data);
        setCryptoData(Array.isArray(data) ? data : []);
      } else {
        console.error('Crypto request failed:', cryptoRes.reason);
      }

      if (stocksRes.status === 'fulfilled') {
        const response = stocksRes.value;
        const data = response.data || response;
        setStocksData(Array.isArray(data) ? data : []);
      }

      if (stockWatchlistRes.status === 'fulfilled') {
        const response = stockWatchlistRes.value;
        const data = response.data || response;
        setStockWatchlist(Array.isArray(data) ? data : []);
      }

      if (newsRes.status === 'fulfilled') {
        const response = newsRes.value;
        const data = response.data || response;
        setNewsData(Array.isArray(data) ? data : []);
      }

      if (bybitRes.status === 'fulfilled') {
        const response = bybitRes.value;
        const data = response.data || response;
        setBybitData(Array.isArray(data) ? data : []);
      }

      if (binanceRes.status === 'fulfilled') {
        const response = binanceRes.value;
        const data = response.data || response;
        setBinanceData(Array.isArray(data) ? data : []);
      }

      if (economicRes.status === 'fulfilled') {
        const response = economicRes.value;
        const data = response.data || response;
        setEconomicData(Array.isArray(data) ? data : []);
      }

      if (syncRes.status === 'fulfilled') {
        const response = syncRes.value;
        const data = response.data || response;
        setSyncStatus(data || {});
      }

      setLastUpdate(new Date());
    } catch (err) {
      setError('Failed to fetch data. Please try again.');
      console.error('Finance data fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num: number | undefined, decimals: number = 2) => {
    if (num === undefined || num === null) return '-';
    return num.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatCurrency = (num: number | undefined, decimals: number = 2) => {
    if (num === undefined || num === null) return '-';
    return '$' + formatNumber(num, decimals);
  };

  const formatPercent = (num: number | undefined) => {
    if (num === undefined || num === null) return '-';
    const prefix = num >= 0 ? '+' : '';
    return `${prefix}${formatNumber(num)}%`;
  };

  const formatVolume = (num: number | undefined) => {
    if (num === undefined || num === null) return '-';
    if (num >= 1e12) return '$' + formatNumber(num / 1e12) + 'T';
    if (num >= 1e9) return '$' + formatNumber(num / 1e9) + 'B';
    if (num >= 1e6) return '$' + formatNumber(num / 1e6) + 'M';
    return '$' + formatNumber(num);
  };

  const tabs = [
    { id: 'overview', label: t('finance.overview'), icon: Activity },
    { id: 'crypto', label: t('finance.crypto'), icon: BarChart3 },
    { id: 'stocks', label: t('finance.stocks'), icon: DollarSign },
    { id: 'bybit', label: t('finance.bybit'), icon: Globe },
    { id: 'binance', label: t('finance.binance'), icon: BookOpen },
    { id: 'economic', label: t('finance.economic'), icon: TrendingUp },
    { id: 'news', label: t('finance.news'), icon: Newspaper },
  ];

  const isSyncError = (source: string) => {
    return syncStatus[source]?.status === 'error';
  };

  const handleCryptoSort = (key: 'rank' | 'coin' | 'price' | 'change24h' | 'volume24h' | 'marketCap') => {
    if (cryptoSortKey === key) {
      setCryptoSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setCryptoSortKey(key);
    setCryptoSortDirection(key === 'coin' ? 'asc' : 'desc');
  };

  const sortedCryptoData = useMemo(() => {
    const sorted = [...cryptoData].sort((a, b) => {
      switch (cryptoSortKey) {
        case 'rank':
          return (b.volume_24h || 0) - (a.volume_24h || 0);
        case 'coin':
          return a.symbol.localeCompare(b.symbol);
        case 'price':
          return (a.price_usd || 0) - (b.price_usd || 0);
        case 'change24h':
          return (a.change_24h_pct || 0) - (b.change_24h_pct || 0);
        case 'marketCap':
          return (a.market_cap || 0) - (b.market_cap || 0);
        case 'volume24h':
        default:
          return (a.volume_24h || 0) - (b.volume_24h || 0);
      }
    });

    if (cryptoSortDirection === 'desc') {
      sorted.reverse();
    }

    return sorted.slice(0, showAllCrypto ? 100 : 20);
  }, [cryptoData, cryptoSortDirection, cryptoSortKey, showAllCrypto]);

  const renderSortIndicator = (key: 'rank' | 'coin' | 'price' | 'change24h' | 'volume24h' | 'marketCap') => {
    if (cryptoSortKey !== key) {
      return <ChevronDown size={14} className="opacity-30" />;
    }

    return cryptoSortDirection === 'asc'
      ? <ChevronUp size={14} className="text-[#58a6ff]" />
      : <ChevronDown size={14} className="text-[#58a6ff]" />;
  };

  const addStock = async () => {
    const symbol = newStockSymbol.trim().toUpperCase();
    if (!symbol) {
      return;
    }

    try {
      setStockActionMessage(null);
      await api.post('/finance/stocks/watchlist', { symbol });
      setNewStockSymbol('');
      setStockActionMessage(`${symbol} added.`);
      await fetchAllData();
    } catch (err) {
      setStockActionMessage(err instanceof Error ? err.message : 'Failed to add stock.');
    }
  };

  const removeStock = async (symbol: string) => {
    try {
      setStockActionMessage(null);
      await api.delete(`/finance/stocks/watchlist/${symbol}`);
      setStockActionMessage(`${symbol} removed.`);
      await fetchAllData();
    } catch (err) {
      setStockActionMessage(err instanceof Error ? err.message : 'Failed to remove stock.');
    }
  };

  const stockRows = stocksData.filter((stock) =>
    stockWatchlist.some((item) => item.symbol === stock.symbol),
  );

  const handleStockSort = (key: 'symbol' | 'name' | 'sector' | 'price' | 'change' | 'volume') => {
    if (stockSortKey === key) {
      setStockSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setStockSortKey(key);
    setStockSortDirection(key === 'symbol' || key === 'name' || key === 'sector' ? 'asc' : 'desc');
  };

  const sortedStockRows = useMemo(() => {
    const sorted = [...stockRows].sort((a, b) => {
      switch (stockSortKey) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'sector':
          return (a.sector || '').localeCompare(b.sector || '');
        case 'price':
          return (a.price || 0) - (b.price || 0);
        case 'change':
          return (a.change_percent || 0) - (b.change_percent || 0);
        case 'volume':
          return (a.volume || 0) - (b.volume || 0);
        case 'symbol':
        default:
          return a.symbol.localeCompare(b.symbol);
      }
    });

    if (stockSortDirection === 'desc') {
      sorted.reverse();
    }

    return sorted;
  }, [stockRows, stockSortDirection, stockSortKey]);

  const sortedNewsData = useMemo(() => {
    return [...newsData].sort((a, b) => {
      const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
      const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [newsData]);

  return (
    <div className="min-h-screen bg-[#0f1419] text-[#e6edf3]">
      {/* Header */}
      <div className="header-gradient border-b border-[#30363d] px-4 py-4 sm:px-6 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gradient sm:text-3xl">{t('finance.title')}</h1>
              <p className="mt-1 text-sm text-[#8b949e] sm:text-base">{t('finance.subtitle')}</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:justify-end">
              <div className="flex items-center gap-2 text-xs text-[#8b949e] sm:text-sm">
                <Clock size={16} />
                <span>{t('finance.lastUpdate')}: {lastUpdate.toLocaleTimeString(language === 'de' ? 'de-DE' : 'en-US')}</span>
              </div>
              <button
                onClick={fetchAllData}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#30363d] bg-[#161b22] px-4 py-2 text-sm text-[#e6edf3] transition-colors hover:bg-[#21262d] disabled:opacity-60 sm:w-auto"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                {t('finance.refresh')}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="mt-4 flex items-center gap-2 text-[#f85149] bg-[#f85149]/10 border border-[#f85149]/30 rounded-lg px-4 py-2">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-[#161b22] border-b border-[#30363d] px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="-mx-4 flex gap-2 overflow-x-auto px-4 py-1 sm:mx-0 sm:px-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-t-lg px-3 py-3 text-sm font-medium transition-all border-b-2 sm:px-4 ${
                    activeTab === tab.id
                      ? 'text-[#58a6ff] border-b-[#58a6ff]'
                      : 'text-[#8b949e] border-b-transparent hover:text-[#e6edf3]'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 sm:py-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card-af p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#8b949e] text-sm">{t('finance.btcPrice')}</p>
                    <p className="text-2xl font-bold text-[#e6edf3]">
                      {formatCurrency(cryptoData[0]?.price_usd)}
                    </p>
                    <p className={`text-sm ${(cryptoData[0]?.change_24h_pct || 0) >= 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                      {formatPercent(cryptoData[0]?.change_24h_pct)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-[#58a6ff]/10 flex items-center justify-center">
                    <BarChart3 size={24} style={{ color: financeColors.crypto }} />
                  </div>
                </div>
                {isSyncError('crypto_prices') && (
                  <p className="text-xs text-[#f85149] mt-2">Sync error</p>
                )}
              </div>

              <div className="card-af p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#8b949e] text-sm">{t('finance.ethPrice')}</p>
                    <p className="text-2xl font-bold text-[#e6edf3]">
                      {formatCurrency(cryptoData[1]?.price_usd)}
                    </p>
                    <p className={`text-sm ${(cryptoData[1]?.change_24h_pct || 0) >= 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                      {formatPercent(cryptoData[1]?.change_24h_pct)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-[#a371f7]/10 flex items-center justify-center">
                    <BarChart3 size={24} style={{ color: financeColors.stocks }} />
                  </div>
                </div>
              </div>

              <div className="card-af p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#8b949e] text-sm">{t('finance.fedFundsRate')}</p>
                    <p className="text-2xl font-bold text-[#e6edf3]">
                      {economicData[0]?.value ? `${economicData[0].value}%` : '-'}
                    </p>
                    <p className="text-sm text-[#8b949e]">
                      {economicData[0]?.date || '-'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-[#f778ba]/10 flex items-center justify-center">
                    <TrendingUp size={24} style={{ color: financeColors.economic }} />
                  </div>
                </div>
              </div>

              <div className="card-af p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#8b949e] text-sm">{t('finance.latestNews')}</p>
                    <p className="text-2xl font-bold text-[#e6edf3]">{newsData.length || 0}</p>
                    <p className="text-sm text-[#8b949e]">{t('finance.today')}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-[#d29922]/10 flex items-center justify-center">
                    <Newspaper size={24} style={{ color: financeColors.news }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Movers and Orderbook Preview */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="card-af p-5">
                <h3 className="text-lg font-semibold text-[#e6edf3] mb-4 flex items-center gap-2">
                  <TrendingUp size={20} style={{ color: financeColors.crypto }} />
                  {t('finance.topCrypto')}
                </h3>
                <div className="space-y-3">
                  {cryptoData.slice(0, 5).map((coin) => (
                    <div key={coin.id} className="flex items-center justify-between py-2 border-b border-[#30363d] last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-[#e6edf3]">{coin.symbol}</span>
                        <span className="text-[#8b949e] text-sm">{coin.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-[#e6edf3]">{formatCurrency(coin.price_usd)}</p>
                        <p className={`text-sm ${(coin.change_24h_pct || 0) >= 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                          {(coin.change_24h_pct || 0) >= 0 ? <ArrowUpRight size={14} className="inline" /> : <ArrowDownRight size={14} className="inline" />}
                          {formatPercent(coin.change_24h_pct)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-af p-5">
                <h3 className="text-lg font-semibold text-[#e6edf3] mb-4 flex items-center gap-2">
                  <Globe size={20} style={{ color: financeColors.bybit }} />
                  {t('finance.bybitPreview')}
                </h3>
                <div className="space-y-3">
                  {bybitData.slice(0, 5).map((ob) => (
                    <div key={ob.id} className="flex items-center justify-between py-2 border-b border-[#30363d] last:border-0">
                      <div>
                        <span className="font-medium text-[#e6edf3]">{ob.symbol}</span>
                        <p className="text-[#8b949e] text-xs">Spread: {((ob.spread_pct || 0) * 100).toFixed(3)}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#e6edf3]">{formatCurrency(ob.mid_price)}</p>
                        <p className={`text-xs ${(ob.imbalance || 0) > 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                          Imbalance: {formatPercent((ob.imbalance || 0) * 100)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-af p-5">
                <h3 className="text-lg font-semibold text-[#e6edf3] mb-4 flex items-center gap-2">
                  <BookOpen size={20} style={{ color: financeColors.bybit }} />
                  {t('finance.binancePreview')}
                </h3>
                <div className="space-y-3">
                  {binanceData.slice(0, 5).map((ob) => (
                    <div key={ob.id} className="flex items-center justify-between py-2 border-b border-[#30363d] last:border-0">
                      <div>
                        <span className="font-medium text-[#e6edf3]">{ob.symbol}</span>
                        <p className="text-[#8b949e] text-xs">Spread: {((ob.spread_pct || 0) * 100).toFixed(3)}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#e6edf3]">{formatCurrency(ob.mid_price)}</p>
                        <p className={`text-xs ${(ob.imbalance || 0) > 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                          Imbalance: {formatPercent((ob.imbalance || 0) * 100)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'crypto' && (
          <div className="card-af p-4 sm:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold text-[#e6edf3]">
                {t('finance.cryptoPrices')}
                <span className="ml-0 block text-sm text-[#8b949e] sm:ml-2 sm:inline">
                  ({showAllCrypto ? t('finance.top100ByVolume') : t('finance.top20ByVolume')})
                </span>
              </h2>
              <button
                onClick={() => setShowAllCrypto(!showAllCrypto)}
                className="w-full rounded-lg bg-[#21262d] px-4 py-2 text-sm text-[#58a6ff] transition-colors hover:bg-[#30363d] sm:w-auto"
              >
                {showAllCrypto ? t('finance.showTop20') : t('finance.showTop100')}
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[#30363d]">
              <table className="min-w-[760px] w-full">
                <thead>
                  <tr className="border-b border-[#30363d]">
                    <th className="px-3 py-3 text-left text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleCryptoSort('rank')} className="inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.rank')}
                        {renderSortIndicator('rank')}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleCryptoSort('coin')} className="inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.coin')}
                        {renderSortIndicator('coin')}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleCryptoSort('price')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.price')}
                        {renderSortIndicator('price')}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleCryptoSort('change24h')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.change24h')}
                        {renderSortIndicator('change24h')}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleCryptoSort('volume24h')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.volume24h')}
                        {renderSortIndicator('volume24h')}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleCryptoSort('marketCap')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.marketCap')}
                        {renderSortIndicator('marketCap')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCryptoData.map((coin, index) => (
                    <tr key={coin.id} className="border-b border-[#30363d]/50 hover:bg-[#1a2332]">
                      <td className="px-3 py-4 text-[#8b949e]">{index + 1}</td>
                      <td className="px-3 py-4">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-[#e6edf3]">{coin.symbol}</span>
                          <span className="text-[#8b949e] text-sm">{coin.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-right text-[#e6edf3]">{formatCurrency(coin.price_usd)}</td>
                      <td className={`px-3 py-4 text-right ${(coin.change_24h_pct || 0) >= 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                        {formatPercent(coin.change_24h_pct)}
                      </td>
                      <td className="px-3 py-4 text-right text-[#8b949e]">{formatVolume(coin.volume_24h)}</td>
                      <td className="px-3 py-4 text-right text-[#8b949e]">{formatVolume(coin.market_cap)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'stocks' && (
          <div className="card-af p-4 sm:p-6">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#e6edf3]">{t('finance.stockPrices')}</h2>
                <p className="mt-1 text-sm text-[#8b949e]">{t('finance.stockWatchlist')}: {stockWatchlist.length}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  value={newStockSymbol}
                  onChange={(event) => setNewStockSymbol(event.target.value.toUpperCase())}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      void addStock();
                    }
                  }}
                  placeholder={t('finance.addStock')}
                  className="rounded-lg border border-[#30363d] bg-[#0f1419] px-4 py-2 text-sm text-[#e6edf3] placeholder:text-[#8b949e]"
                />
                <button
                  type="button"
                  onClick={() => void addStock()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#238636] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2ea043]"
                >
                  <Plus size={16} />
                  {t('finance.add')}
                </button>
              </div>
            </div>
            {stockActionMessage && (
              <div className="mb-4 rounded-lg border border-[#30363d] bg-[#0f1419] px-4 py-3 text-sm text-[#8b949e]">
                {stockActionMessage}
              </div>
            )}
            <div className="overflow-x-auto rounded-lg border border-[#30363d]">
              <table className="min-w-[860px] w-full">
                <thead>
                  <tr className="border-b border-[#30363d]">
                    <th className="px-3 py-3 text-left text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleStockSort('symbol')} className="inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.symbol')}
                        {stockSortKey === 'symbol' && stockSortDirection === 'asc' ? <ChevronUp size={14} className="text-[#58a6ff]" /> : <ChevronDown size={14} className={stockSortKey === 'symbol' ? 'text-[#58a6ff]' : 'opacity-30'} />}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleStockSort('name')} className="inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.name')}
                        {stockSortKey === 'name' && stockSortDirection === 'asc' ? <ChevronUp size={14} className="text-[#58a6ff]" /> : <ChevronDown size={14} className={stockSortKey === 'name' ? 'text-[#58a6ff]' : 'opacity-30'} />}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleStockSort('sector')} className="inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.sector')}
                        {stockSortKey === 'sector' && stockSortDirection === 'asc' ? <ChevronUp size={14} className="text-[#58a6ff]" /> : <ChevronDown size={14} className={stockSortKey === 'sector' ? 'text-[#58a6ff]' : 'opacity-30'} />}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleStockSort('price')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.price')}
                        {stockSortKey === 'price' && stockSortDirection === 'asc' ? <ChevronUp size={14} className="text-[#58a6ff]" /> : <ChevronDown size={14} className={stockSortKey === 'price' ? 'text-[#58a6ff]' : 'opacity-30'} />}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleStockSort('change')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.change')}
                        {stockSortKey === 'change' && stockSortDirection === 'asc' ? <ChevronUp size={14} className="text-[#58a6ff]" /> : <ChevronDown size={14} className={stockSortKey === 'change' ? 'text-[#58a6ff]' : 'opacity-30'} />}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">
                      <button type="button" onClick={() => handleStockSort('volume')} className="ml-auto inline-flex items-center gap-1 hover:text-[#e6edf3]">
                        {t('finance.volume')}
                        {stockSortKey === 'volume' && stockSortDirection === 'asc' ? <ChevronUp size={14} className="text-[#58a6ff]" /> : <ChevronDown size={14} className={stockSortKey === 'volume' ? 'text-[#58a6ff]' : 'opacity-30'} />}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-[#8b949e] font-medium">{t('finance.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStockRows.map((stock) => (
                    <tr key={stock.id} className="border-b border-[#30363d]/50 hover:bg-[#1a2332]">
                      <td className="px-3 py-4 font-semibold text-[#e6edf3]">{stock.symbol}</td>
                      <td className="px-3 py-4 text-[#8b949e]">{stock.name}</td>
                      <td className="px-3 py-4 text-[#8b949e]">{stock.sector || '-'}</td>
                      <td className="px-3 py-4 text-right text-[#e6edf3]">{formatCurrency(stock.price)}</td>
                      <td className={`px-3 py-4 text-right ${(stock.change_percent || 0) >= 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                        {formatPercent(stock.change_percent)}
                      </td>
                      <td className="px-3 py-4 text-right text-[#8b949e]">{(stock.volume || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US')}</td>
                      <td className="px-3 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => void removeStock(stock.symbol)}
                          className="inline-flex items-center gap-2 rounded-lg border border-[#f85149]/40 px-3 py-2 text-sm text-[#f85149] transition-colors hover:bg-[#f85149]/10"
                        >
                          <Trash2 size={14} />
                          {t('finance.remove')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'bybit' && (
          <div className="card-af p-4 sm:p-6">
            <h2 className="mb-6 text-xl font-semibold text-[#e6edf3]">{t('finance.bybitTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bybitData.map((ob) => (
                <div key={ob.id} className="bg-[#0f1419] border border-[#30363d] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-[#e6edf3]">{ob.symbol}</span>
                    <span className="text-[#58a6ff] text-sm">{t('finance.live')}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.midPrice')}</span>
                      <span className="text-[#e6edf3]">{formatCurrency(ob.mid_price)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.spread')}</span>
                      <span className="text-[#e6edf3]">{((ob.spread_pct || 0) * 100).toFixed(4)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.bidDepth')}</span>
                      <span className="text-[#e6edf3]">{formatVolume(ob.bid_depth)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.askDepth')}</span>
                      <span className="text-[#e6edf3]">{formatVolume(ob.ask_depth)}</span>
                    </div>
                    <div className="pt-2 border-t border-[#30363d]">
                      <div className="flex justify-between">
                        <span className="text-[#8b949e] text-sm">{t('finance.imbalance')}</span>
                        <span className={`${(ob.imbalance || 0) > 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                          {formatPercent((ob.imbalance || 0) * 100)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'binance' && (
          <div className="card-af p-4 sm:p-6">
            <h2 className="mb-6 text-xl font-semibold text-[#e6edf3]">{t('finance.binanceTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {binanceData.map((ob) => (
                <div key={ob.id} className="bg-[#0f1419] border border-[#30363d] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-[#e6edf3]">{ob.symbol}</span>
                    <span className="text-[#58a6ff] text-sm">{t('finance.live')}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.midPrice')}</span>
                      <span className="text-[#e6edf3]">{formatCurrency(ob.mid_price)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.spread')}</span>
                      <span className="text-[#e6edf3]">{((ob.spread_pct || 0) * 100).toFixed(4)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.bidDepth')}</span>
                      <span className="text-[#e6edf3]">{formatVolume(ob.bid_depth)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b949e] text-sm">{t('finance.askDepth')}</span>
                      <span className="text-[#e6edf3]">{formatVolume(ob.ask_depth)}</span>
                    </div>
                    <div className="pt-2 border-t border-[#30363d]">
                      <div className="flex justify-between">
                        <span className="text-[#8b949e] text-sm">{t('finance.imbalance')}</span>
                        <span className={`${(ob.imbalance || 0) > 0 ? 'text-[#3fb950]' : 'text-[#f85149]'}`}>
                          {formatPercent((ob.imbalance || 0) * 100)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'economic' && (
          <div className="card-af p-4 sm:p-6">
            <h2 className="mb-6 text-xl font-semibold text-[#e6edf3]">{t('finance.economicIndicators')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {economicData.map((indicator) => (
                <div key={indicator.id} className="bg-[#0f1419] border border-[#30363d] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#8b949e] text-sm">{indicator.name}</p>
                      <p className="text-2xl font-bold text-[#e6edf3]">
                        {indicator.indicator_id === 'FEDFUNDS' || indicator.indicator_id === 'UNRATE'
                          ? `${indicator.value}%`
                          : formatNumber(indicator.value)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#8b949e] text-xs">{indicator.date || '-'}</p>
                      <p className="text-[#58a6ff] text-xs">{indicator.indicator_id}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'news' && (
          <div className="card-af p-4 sm:p-6">
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#e6edf3]">{t('finance.latestNewsTitle')}</h2>
                <p className="mt-1 text-sm text-[#8b949e]">{t('finance.newsSortedHint')}</p>
              </div>
            </div>
            <div className="space-y-4">
              {sortedNewsData.map((news) => {
                const isRead = readNewsIds.includes(news.id);
                return (
                <div
                  key={news.id}
                  className={`bg-[#0f1419] border rounded-lg p-4 transition-colors ${
                    isRead
                      ? 'border-[#30363d] opacity-80'
                      : 'border-[#30363d] hover:border-[#58a6ff]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      {news.url ? (
                        <a
                          href={news.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => markNewsAsRead(news.id)}
                          className={`mb-2 inline-flex items-start gap-2 font-medium ${
                            isRead ? 'text-[#8b949e]' : 'text-[#58a6ff] hover:text-[#79c0ff]'
                          }`}
                        >
                          <span>{news.title}</span>
                          <ExternalLink size={16} className="mt-0.5 shrink-0" />
                        </a>
                      ) : (
                        <h3 className={`${isRead ? 'text-[#8b949e]' : 'text-[#e6edf3]'} font-medium mb-2`}>{news.title}</h3>
                      )}
                      <div className="flex flex-wrap items-center gap-2 text-sm text-[#8b949e]">
                        <span className="px-2 py-0.5 rounded bg-[#21262d] text-[#58a6ff]">{news.source}</span>
                        <span className="px-2 py-0.5 rounded bg-[#21262d] text-[#8b949e]">{news.category}</span>
                        <span className={`px-2 py-0.5 rounded ${isRead ? 'bg-[#1f2937] text-[#c49243]' : 'bg-[#21262d] text-[#8b949e]'}`}>
                          {isRead ? t('finance.read') : t('finance.unread')}
                        </span>
                        <span>{new Date(news.published_at).toLocaleString(language === 'de' ? 'de-DE' : 'en-US')}</span>
                      </div>
                    </div>
                    <div className="ml-1 flex shrink-0 flex-col items-end gap-3 sm:ml-4">
                      <BookOpen size={20} className="text-[#8b949e]" />
                      <button
                        type="button"
                        onClick={() => markNewsAsRead(news.id)}
                        className={`rounded-lg border px-3 py-2 text-xs transition-colors ${
                          isRead
                            ? 'border-[#c49243]/40 text-[#c49243] bg-[#c49243]/10'
                            : 'border-[#30363d] text-[#e6edf3] hover:border-[#58a6ff] hover:text-[#58a6ff]'
                        }`}
                      >
                        {t('finance.markRead')}
                      </button>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
